import FavoritesSearch from './FavoritesSearch'
import FavoriteCard from './FavoriteCard'
import FavoritesLoadingSkeleton from './FavoritesLoadingSkeleton'

interface Card {
  card_id: number
  title: string
  icon_url: string
}

interface FavoritesSectionProps {
  hasActiveSubscription: () => boolean
  favorites: string[]
  favoritesLoading: boolean
  cardsLoading: boolean
  categoriesLoading: boolean
  favoriteCards: Card[]
  hierarchicalFavoriteCards: any
  searchQuery: string
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClearSearch: () => void
  onToggleFavorite: (itemId: string) => void
}

const FavoritesSection = ({
  hasActiveSubscription,
  favorites,
  favoritesLoading,
  cardsLoading,
  categoriesLoading,
  favoriteCards,
  hierarchicalFavoriteCards,
  searchQuery,
  onSearchChange,
  onClearSearch,
  onToggleFavorite,
}: FavoritesSectionProps) => {
  if (!hasActiveSubscription()) {
    return null
  }

  return (
    <div className="profile-section-favorites">
      <h2 className="section-title">Избранное</h2>

      {favoritesLoading || cardsLoading || categoriesLoading ? (
        <FavoritesLoadingSkeleton />
      ) : favorites.length === 0 ? (
        // Состояние: нет избранного вообще - не показываем поиск
        <div className="empty-favorites">
          <p>Вы пока ничего не добавили в Избранное</p>
        </div>
      ) : (
        <>
          <FavoritesSearch
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            onClearSearch={onClearSearch}
          />

          {favoriteCards.length > 0 ? (
            searchQuery.trim() ? (
              // Показываем плоский список при поиске
              <div className="favorites-search-results">
                <div className="favorites-grid">
                  {favoriteCards.map((card: Card) => {
                    const cardId = card.card_id.toString()
                    return (
                      <FavoriteCard
                        key={card.card_id}
                        card={card}
                        onToggleFavorite={onToggleFavorite}
                        isFavorite={favorites.includes(cardId)}
                        searchQuery={searchQuery}
                        hasActiveSubscription={hasActiveSubscription}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              // Показываем иерархию без поиска
              <div className="favorites-hierarchical">
                {Object.values(hierarchicalFavoriteCards).map(
                  (mainCategory: any) => {
                    // Подсчитываем общее количество карточек в главной категории
                    const mainCategoryCount = Object.values(
                      mainCategory.subcategories
                    ).reduce((total: number, subCategory: any) => {
                      return (
                        total +
                        Object.values(subCategory.subSubcategories).reduce(
                          (subTotal: number, subSubCategory: any) =>
                            subTotal + subSubCategory.cards.length,
                          0
                        )
                      )
                    }, 0)

                    return (
                      <div
                        key={mainCategory.main.category_id}
                        className="favorites-main-group"
                      >
                        <h3 className="favorites-main-category">
                          {mainCategory.main.name}
                          <span className="category-count">
                            [{mainCategoryCount}]
                          </span>
                        </h3>

                        {Object.values(mainCategory.subcategories).map(
                          (subCategory: any) => {
                            // Подсчитываем количество карточек в подкатегории
                            const subCategoryCount = Object.values(
                              subCategory.subSubcategories
                            ).reduce(
                              (total: number, subSubCategory: any) =>
                                total + subSubCategory.cards.length,
                              0
                            )

                            return Object.values(
                              subCategory.subSubcategories
                            ).map((subSubCategory: any) => (
                              <div
                                key={
                                  subSubCategory.subSub
                                    ? subSubCategory.subSub.category_id
                                    : subCategory.sub.category_id
                                }
                                className="favorites-subsub-group"
                              >
                                <h4 className="favorites-subsub-category">
                                  {subSubCategory.subSub
                                    ? subSubCategory.subSub.name
                                    : subCategory.sub.name}
                                  <span className="category-count">
                                    [{subSubCategory.cards.length}]
                                  </span>
                                </h4>

                                <div className="favorites-grid">
                                  {subSubCategory.cards.map((card: Card) => {
                                    const cardId = card.card_id.toString()
                                    return (
                                      <FavoriteCard
                                        key={card.card_id}
                                        card={card}
                                        onToggleFavorite={onToggleFavorite}
                                        isFavorite={favorites.includes(cardId)}
                                        searchQuery={searchQuery}
                                        hasActiveSubscription={
                                          hasActiveSubscription
                                        }
                                      />
                                    )
                                  })}
                                </div>
                              </div>
                            ))
                          }
                        )}
                      </div>
                    )
                  }
                )}
              </div>
            )
          ) : searchQuery.trim() ? (
            // Состояние: есть поисковый запрос, но нет совпадений
            <div className="empty-search-results">
              <p>Мы ничего не нашли, попробуйте подругому написать запрос</p>
            </div>
          ) : (
            // Состояние: есть избранное, но после фильтрации ничего не осталось (не должно происходить)
            <div className="empty-favorites">
              <p>Вы пока не добавили ни одной карточки в избранное</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default FavoritesSection
